import React, { useState, Children, Component } from 'react';
import { motion } from 'framer-motion';
import { Droplets, Leaf, Sparkles, Heart, ArrowDown, Zap, Users, Sun, Instagram, Bell, Building2, Check, ArrowRight, Mail } from 'lucide-react';
// Brand constants
const BRAND_COLOR = '#B33A3A';
const LOGO_URL = 'https://cdn.magicpatterns.com/uploads/v2dXUg39fLTFosJrhxryFj/3Beet_logo.png';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/m4VHGC1GgxgtGcoFZGpH81/IMG_0693.jpg';
const BEETROOT_IMAGE = 'https://cdn.magicpatterns.com/uploads/2ccD6uQTuFQxiaojXP4f8n/IMG_0691.jpg';
const DJ_IMAGE = 'https://cdn.magicpatterns.com/uploads/8d4yzQkioQtxdxbPPr3ug5/IMG_0695.jpg';
const INSTAGRAM_URL = 'https://www.instagram.com/beatyourbest_?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==';
const CONTACT_EMAIL = 'info@beatsoda.com';
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.1
    }
  }
};
export function LandingPage() {
  const scrollToHeartOfBeat = () => {
    const element = document.getElementById('heart-of-beat');
    element?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <div className="min-h-screen w-full bg-white text-gray-900 font-sans selection:bg-[#B33A3A] selection:text-white overflow-x-hidden" data-id="element-1">
      {/* Hero Section */}
      <section className="min-h-screen flex flex-col items-center justify-center px-6 py-20 relative bg-white" data-id="element-2">
        <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center text-center max-w-6xl mx-auto" data-id="element-3">
          <motion.div variants={fadeInUp} className="mb-8 md:mb-12 w-full" data-id="element-4">
            <img src={LOGO_URL} alt="BEAT Soda" className="w-full max-w-[80vw] md:max-w-[60vw] lg:max-w-[50vw] h-auto mx-auto" data-id="element-5" />
          </motion.div>

          <motion.h1 variants={fadeInUp} className="text-2xl md:text-4xl lg:text-5xl font-medium tracking-tight text-gray-900 mb-6 leading-tight" data-id="element-6">
            We don't drink to disappear.
            <br data-id="element-7" />
            <span style={{
            color: BRAND_COLOR
          }} data-id="element-8">
              We drink to arrive.
            </span>
          </motion.h1>

          <motion.p variants={fadeInUp} className="text-lg md:text-xl text-gray-500 max-w-xl mb-10 leading-relaxed font-light" data-id="element-9">
            A sober soda for those who choose presence over excess.
          </motion.p>

          <motion.button variants={fadeInUp} whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={scrollToHeartOfBeat} className="px-10 py-4 rounded-full text-white font-medium text-lg transition-colors shadow-lg hover:shadow-xl tracking-wide" style={{
          backgroundColor: BRAND_COLOR
        }} data-id="element-10">
            Discover BEAT
          </motion.button>
        </motion.div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 2,
        duration: 1
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2 text-gray-300 animate-bounce" data-id="element-11">
          <ArrowDown size={24} data-id="element-12" />
        </motion.div>
      </section>

      {/* Full Screen Product Image */}
      <section className="h-screen w-full relative" data-id="element-13">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="w-full h-full" data-id="element-14">
          <img src={HERO_IMAGE} alt="BEAT soda can in a social setting" className="w-full h-full object-cover" data-id="element-15" />
        </motion.div>
      </section>

      {/* Manifesto Section */}
      <section id="manifesto" className="py-32 md:py-48 px-6 bg-white" data-id="element-16">
        <div className="max-w-3xl mx-auto text-center" data-id="element-17">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="space-y-12" data-id="element-18">
            <motion.p variants={fadeInUp} className="text-3xl md:text-5xl font-light leading-tight text-gray-800" data-id="element-19">
              Life is meant to be felt.
            </motion.p>
            <motion.p variants={fadeInUp} className="text-2xl md:text-4xl font-light text-gray-500 italic" data-id="element-20">
              Not dulled. Not rushed through.
              <br data-id="element-21" />
              Not forgotten.
            </motion.p>
            <motion.div variants={fadeInUp} className="w-16 h-1 mx-auto rounded-full my-12" style={{
            backgroundColor: BRAND_COLOR
          }} data-id="element-22" />
            <motion.p variants={fadeInUp} className="text-3xl md:text-5xl font-medium text-gray-900" data-id="element-23">
              Every heartbeat matters.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Beetroot Can Image */}
      <section className="h-screen w-full relative" data-id="element-24">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="w-full h-full" data-id="element-25">
          <img src={BEETROOT_IMAGE} alt="BEAT soda can showing beetroot illustration" className="w-full h-full object-cover" data-id="element-26" />
        </motion.div>
      </section>

      {/* For Who Section */}
      <section className="py-32 px-6 bg-gray-50" data-id="element-27">
        <div className="max-w-6xl mx-auto" data-id="element-28">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-29">
            <h2 className="text-sm font-bold tracking-widest uppercase text-gray-400 mb-4" data-id="element-30">
              For those who want to
            </h2>
            <p className="text-3xl md:text-4xl font-medium text-gray-900" data-id="element-31">
              Bring their best
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8" data-id="element-32">
            <PersonaCard icon={<Zap size={32} data-id="element-34" />} title="Create" description="Ideas that spark, minds that stay sharp, and focus that never fades." data-id="element-33" />
            <PersonaCard icon={<Users size={32} data-id="element-36" />} title="Connect" description="Conversations that land, moments that matter, and presence that is felt." data-id="element-35" />
            <PersonaCard icon={<Sun size={32} data-id="element-38" />} title="Contribute" description="Bodies that feel alive the next day, ready to give more to the world." data-id="element-37" />
          </motion.div>
        </div>
      </section>

      {/* Why Beetroot Section */}
      <section id="heart-of-beat" className="py-32 px-6 bg-white overflow-hidden relative" data-id="element-39">
        <div className="max-w-4xl mx-auto relative z-10" data-id="element-40">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-20" data-id="element-41">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-50 mb-8" data-id="element-42">
              <Heart size={36} className="text-[#B33A3A]" data-id="element-43" />
            </div>
            <h2 className="text-4xl md:text-5xl font-medium mb-6" data-id="element-44">
              The heart of BEAT
            </h2>
            <p className="text-xl text-gray-500 max-w-2xl mx-auto" data-id="element-45">
              We built BEAT around the beetroot for a reason. It's nature's way
              of keeping the rhythm.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-10 mb-24" data-id="element-46">
            <BenefitItem title="Natural energy" description="Beetroot's nitrates support blood flow and vitality, giving you a lift without the crash." data-id="element-47" />
            <BenefitItem title="Heart-forward" description="A drink that literally supports your heart, matching our philosophy of living with passion." data-id="element-48" />
            <BenefitItem title="Earthy depth" description="Complex, grown-up flavor profiles that challenge the palate without artificial sweeteners." data-id="element-49" />
            <BenefitItem title="The name says it all" description="BEAT. Beet. Heartbeat. It all connects to the rhythm of a life lived fully." data-id="element-50" />
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="flex flex-col items-center text-center border-t border-gray-100 pt-16" data-id="element-51">
            <h3 className="text-sm font-bold tracking-widest uppercase text-gray-400 mb-8" data-id="element-52">
              Pure Ingredients
            </h3>
            <div className="flex flex-wrap justify-center gap-x-3 gap-y-2 max-w-2xl text-sm md:text-base font-medium text-gray-600 tracking-wide uppercase" data-id="element-53">
              <span data-id="element-54">Beetroot</span>
              <span className="text-gray-300" data-id="element-55">•</span>
              <span data-id="element-56">Grapefruit</span>
              <span className="text-gray-300" data-id="element-57">•</span>
              <span data-id="element-58">Elderflower</span>
              <span className="text-gray-300" data-id="element-59">•</span>
              <span data-id="element-60">Lemon</span>
              <span className="text-gray-300" data-id="element-61">•</span>
              <span data-id="element-62">Prebiotics</span>
              <span className="text-gray-300" data-id="element-63">•</span>
              <span data-id="element-64">Apple Cider Vinegar</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* DJ Image */}
      <section className="h-screen w-full relative" data-id="element-65">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="w-full h-full" data-id="element-66">
          <img src={DJ_IMAGE} alt="BEAT soda can next to DJ equipment" className="w-full h-full object-cover" data-id="element-67" />
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-6 bg-gray-50" data-id="element-68">
        <div className="max-w-4xl mx-auto" data-id="element-69">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeInUp} className="text-center mb-16" data-id="element-70">
            <h2 className="text-4xl md:text-5xl font-medium mb-6" data-id="element-71">
              Join the movement
            </h2>
            <p className="text-xl text-gray-500 mb-10" data-id="element-72">
              Be part of the rhythm.
            </p>

            {/* Email Contact - Prominent Display */}
            <div className="inline-flex flex-col items-center" data-id="element-73">
              <p className="text-sm font-medium tracking-widest uppercase text-gray-400 mb-3" data-id="element-74">
                Get in touch
              </p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-2xl md:text-3xl font-medium text-[#B33A3A] hover:opacity-80 transition-opacity" data-id="element-75">
                {CONTACT_EMAIL}
              </a>
            </div>
          </motion.div>

          {/* CTA Cards - Compact Layout */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} variants={staggerContainer} className="space-y-4" data-id="element-76">
            {/* Instagram */}
            <motion.a variants={fadeInUp} href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="group bg-white p-5 rounded-2xl shadow-sm hover:shadow-md transition-all flex items-center gap-4" data-id="element-77">
              <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center text-[#B33A3A] flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-78">
                <Instagram size={24} data-id="element-79" />
              </div>
              <div className="text-left flex-1" data-id="element-80">
                <h3 className="font-medium text-gray-900" data-id="element-81">Follow us</h3>
                <p className="text-sm text-gray-500" data-id="element-82">@beatyourbest_</p>
              </div>
              <ArrowRight size={18} className="text-gray-300 group-hover:text-[#B33A3A] group-hover:translate-x-1 transition-all" data-id="element-83" />
            </motion.a>

            {/* Launch Notification */}
            <NotifyCardCompact data-id="element-84" />

            {/* B2B Inquiry */}
            <BusinessCardCompact data-id="element-85" />
          </motion.div>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-40 px-6 bg-[#B33A3A] text-white text-center" data-id="element-86">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true
      }} variants={staggerContainer} className="max-w-4xl mx-auto flex flex-col items-center" data-id="element-87">
          <motion.h2 variants={fadeInUp} className="text-4xl md:text-7xl font-medium mb-8 leading-tight" data-id="element-88">
            Don't waste a heartbeat.
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-2xl md:text-4xl font-light opacity-90 mb-12" data-id="element-89">
            Drink BEAT.
          </motion.p>
          <motion.p variants={fadeInUp} className="text-lg md:text-xl tracking-widest uppercase opacity-70 font-light" data-id="element-90">
            A sober soda, done right.
          </motion.p>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-white" data-id="element-91">
        <div className="max-w-4xl mx-auto px-6 flex flex-col items-center justify-center gap-8" data-id="element-92">
          <img src={LOGO_URL} alt="BEAT Soda Logo" className="w-24 opacity-80 grayscale hover:grayscale-0 transition-all duration-300" data-id="element-93" />
          <a href={`mailto:${CONTACT_EMAIL}`} className="text-sm text-gray-500 hover:text-[#B33A3A] transition-colors" data-id="element-94">
            {CONTACT_EMAIL}
          </a>
          <div className="flex flex-col items-center gap-2 text-sm text-gray-400" data-id="element-95">
            <p data-id="element-96">A sober soda, done right.</p>
            <p data-id="element-97">© {new Date().getFullYear()} BEAT Soda. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>;
}
function BenefitItem({
  title,
  description
}: {
  title: string;
  description: string;
}) {
  return <motion.div variants={fadeInUp} className="flex flex-col items-start text-left" data-id="element-98">
      <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center text-[#B33A3A] mb-4" data-id="element-99">
        <Heart size={18} data-id="element-100" />
      </div>
      <h3 className="text-xl font-medium text-gray-900 mb-2" data-id="element-101">{title}</h3>
      <p className="text-gray-500 leading-relaxed" data-id="element-102">{description}</p>
    </motion.div>;
}
function PersonaCard({
  icon,
  title,
  description
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return <motion.div variants={fadeInUp} className="flex flex-col items-center text-center p-8" data-id="element-103">
      <div className="mb-8 text-[#B33A3A]" data-id="element-104">{icon}</div>
      <h3 className="text-2xl font-medium mb-4 text-gray-900" data-id="element-105">{title}</h3>
      <p className="text-lg text-gray-500 leading-relaxed" data-id="element-106">{description}</p>
    </motion.div>;
}
function NotifyCard() {
  const [email, setEmail] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      const subject = encodeURIComponent('Notify me about BEAT launch');
      const body = encodeURIComponent(`Hi BEAT team,\n\nPlease notify me when BEAT launches!\n\nMy email: ${email}\n\nThanks`);
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2
  }} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center" data-id="element-107">
      <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center text-[#B33A3A] mb-5" data-id="element-108">
        <Bell size={28} data-id="element-109" />
      </div>
      <h3 className="text-xl font-medium mb-2" data-id="element-110">Be first to know</h3>
      <p className="text-gray-500 mb-6" data-id="element-111">Get notified when we launch.</p>
      <form onSubmit={handleSubmit} className="w-full space-y-3" data-id="element-112">
        <input type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-center" data-id="element-113" />
        <button type="submit" className="w-full px-6 py-2.5 rounded-full bg-[#B33A3A] text-white text-sm font-medium hover:bg-[#9a3232] transition-colors" data-id="element-114">
          Notify Me
        </button>
      </form>
    </motion.div>;
}
function NotifyCardCompact() {
  const [email, setEmail] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'a23b9419-6869-4092-bd1e-0a48a2461c5e',
          subject: 'New BEAT Launch Notification Signup',
          from_name: 'BEAT Website',
          to: CONTACT_EMAIL,
          email: email,
          message: `New launch notification signup:\n\nEmail: ${email}\n\nThis person wants to be notified when BEAT launches.`
        })
      });
      if (response.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };
  return <motion.div variants={fadeInUp} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden" data-id="element-115">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 flex items-center gap-4 text-left" data-id="element-116">
        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center text-[#B33A3A] flex-shrink-0" data-id="element-117">
          <Bell size={24} data-id="element-118" />
        </div>
        <div className="flex-1" data-id="element-119">
          <h3 className="font-medium text-gray-900" data-id="element-120">Get notified</h3>
          <p className="text-sm text-gray-500" data-id="element-121">
            Be first to know when we launch
          </p>
        </div>
        <ArrowRight size={18} className={`text-gray-300 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-122" />
      </button>

      {expanded && <div className="px-5 pb-5" data-id="element-123">
          {status === 'success' ? <div className="flex items-center gap-3 text-green-600 bg-green-50 px-4 py-3 rounded-xl" data-id="element-124">
              <Check size={20} data-id="element-125" />
              <span className="text-sm font-medium" data-id="element-126">
                You're on the list! We'll be in touch.
              </span>
            </div> : <form onSubmit={handleSubmit} className="flex gap-3" data-id="element-127">
              <input type="email" placeholder="Your email" value={email} onChange={e => setEmail(e.target.value)} required disabled={status === 'loading'} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-sm disabled:opacity-50" data-id="element-128" />
              <button type="submit" disabled={status === 'loading'} className="px-5 py-2.5 rounded-full bg-[#B33A3A] text-white text-sm font-medium hover:bg-[#9a3232] transition-colors whitespace-nowrap disabled:opacity-50" data-id="element-129">
                {status === 'loading' ? 'Sending...' : 'Notify Me'}
              </button>
            </form>}
          {status === 'error' && <p className="text-red-500 text-sm mt-2" data-id="element-130">
              Something went wrong. Please try again.
            </p>}
        </div>}
    </motion.div>;
}
function BusinessCard() {
  const [formState, setFormState] = useState({
    company: '',
    email: ''
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formState.email && formState.company) {
      const subject = encodeURIComponent('Stock BEAT - Business Inquiry');
      const body = encodeURIComponent(`Hi BEAT team,\n\nI'm interested in stocking BEAT at my business.\n\nCompany: ${formState.company}\nEmail: ${formState.email}\n\nThanks`);
      window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.3
  }} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow flex flex-col items-center text-center" data-id="element-131">
      <div className="w-14 h-14 bg-stone-100 rounded-full flex items-center justify-center text-stone-600 mb-5" data-id="element-132">
        <Building2 size={28} data-id="element-133" />
      </div>
      <h3 className="text-xl font-medium mb-2" data-id="element-134">Stock BEAT</h3>
      <p className="text-gray-500 mb-6" data-id="element-135">For bars, cafes & retailers.</p>
      <form onSubmit={handleSubmit} className="w-full space-y-3" data-id="element-136">
        <input type="text" placeholder="Company name" value={formState.company} onChange={e => setFormState({
        ...formState,
        company: e.target.value
      })} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-center" data-id="element-137" />
        <input type="email" placeholder="Your email" value={formState.email} onChange={e => setFormState({
        ...formState,
        email: e.target.value
      })} required className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-center" data-id="element-138" />
        <button type="submit" className="w-full px-6 py-2.5 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-black transition-colors" data-id="element-139">
          Get in Touch
        </button>
      </form>
    </motion.div>;
}
function BusinessCardCompact() {
  const [formState, setFormState] = useState({
    company: '',
    email: '',
    notes: ''
  });
  const [expanded, setExpanded] = useState(false);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formState.email || !formState.company) return;
    setStatus('loading');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: 'a23b9419-6869-4092-bd1e-0a48a2461c5e',
          subject: 'New Stock BEAT Business Inquiry',
          from_name: 'BEAT Website',
          to: CONTACT_EMAIL,
          company: formState.company,
          email: formState.email,
          notes: formState.notes || 'No additional notes provided',
          message: `New business inquiry to stock BEAT:\n\nCompany: ${formState.company}\nEmail: ${formState.email}\nNotes: ${formState.notes || 'No additional notes provided'}\n\nThis business is interested in stocking BEAT.`
        })
      });
      if (response.ok) {
        setStatus('success');
        setFormState({
          company: '',
          email: '',
          notes: ''
        });
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };
  return <motion.div variants={fadeInUp} className="bg-white rounded-2xl shadow-sm hover:shadow-md transition-all overflow-hidden" data-id="element-140">
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 flex items-center gap-4 text-left" data-id="element-141">
        <div className="w-12 h-12 bg-stone-100 rounded-full flex items-center justify-center text-stone-600 flex-shrink-0" data-id="element-142">
          <Building2 size={24} data-id="element-143" />
        </div>
        <div className="flex-1" data-id="element-144">
          <h3 className="font-medium text-gray-900" data-id="element-145">Stock BEAT</h3>
          <p className="text-sm text-gray-500" data-id="element-146">For bars, cafes & retailers</p>
        </div>
        <ArrowRight size={18} className={`text-gray-300 transition-transform ${expanded ? 'rotate-90' : ''}`} data-id="element-147" />
      </button>

      {expanded && <div className="px-5 pb-5" data-id="element-148">
          {status === 'success' ? <div className="flex items-center gap-3 text-green-600 bg-green-50 px-4 py-3 rounded-xl" data-id="element-149">
              <Check size={20} data-id="element-150" />
              <span className="text-sm font-medium" data-id="element-151">
                Thanks! We'll be in touch soon.
              </span>
            </div> : <form onSubmit={handleSubmit} className="flex flex-col gap-3" data-id="element-152">
              <div className="flex flex-col sm:flex-row gap-3" data-id="element-153">
                <input type="text" placeholder="Company name" value={formState.company} onChange={e => setFormState({
            ...formState,
            company: e.target.value
          })} required disabled={status === 'loading'} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-sm disabled:opacity-50" data-id="element-154" />
                <input type="email" placeholder="Your email" value={formState.email} onChange={e => setFormState({
            ...formState,
            email: e.target.value
          })} required disabled={status === 'loading'} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-sm disabled:opacity-50" data-id="element-155" />
              </div>
              <textarea placeholder="Tell us about your interest (e.g., quantity, type of venue, timeline...)" value={formState.notes} onChange={e => setFormState({
          ...formState,
          notes: e.target.value
        })} disabled={status === 'loading'} rows={3} className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[#B33A3A]/20 focus:border-[#B33A3A] transition-all text-sm disabled:opacity-50 resize-none" data-id="element-156" />
              <button type="submit" disabled={status === 'loading'} className="self-end px-5 py-2.5 rounded-full bg-gray-900 text-white text-sm font-medium hover:bg-black transition-colors whitespace-nowrap disabled:opacity-50" data-id="element-157">
                {status === 'loading' ? 'Sending...' : 'Get in Touch'}
              </button>
            </form>}
          {status === 'error' && <p className="text-red-500 text-sm mt-2" data-id="element-158">
              Something went wrong. Please try again.
            </p>}
        </div>}
    </motion.div>;
}